// src/components/pages/surveys/EditSurveyScreen.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Button, 
  TextField, 
  Grid, 
  IconButton, 
  FormControlLabel, 
  Switch, 
  Divider, 
  Card, 
  CardContent, 
  CardActions, 
  List, 
  ListItem, 
  ListItemText, 
  Chip, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  CircularProgress, 
  RadioGroup, 
  Radio, 
  FormControl, 
  FormLabel,
  FormGroup,
  Checkbox
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  ArrowUpward as ArrowUpwardIcon, 
  ArrowDownward as ArrowDownwardIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

// Usado para generar IDs únicos para preguntas nuevas
const generateUniqueId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const QUESTION_TYPES = [
  { label: 'Opción múltiple', value: 'multiple' },
  { label: 'Selección única', value: 'single' },
  { label: 'Texto', value: 'text' },
  { label: 'Sí/No', value: 'boolean' }
];

const EditSurveyScreen = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Estado para el diálogo de nueva pregunta
  const [questionDialogVisible, setQuestionDialogVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState('multiple');
  const [questionOptions, setQuestionOptions] = useState(['']);
  const [questionRequired, setQuestionRequired] = useState(true);
  
  // Cargar datos de la encuesta
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const surveyDoc = await getDoc(doc(db, 'surveys', surveyId));
        if (surveyDoc.exists()) {
          setSurvey({ id: surveyDoc.id, ...surveyDoc.data() });
        } else {
          // Usar soluciones de alerta de Material-UI si lo deseamos
          navigate('/admin/surveys');
        }
      } catch (error) {
        console.error("Error al cargar encuesta:", error);
        navigate('/admin/surveys');
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [surveyId, navigate]);

  // Guardar cambios en la encuesta
  const handleSaveSurvey = async () => {
    if (!survey.título.trim()) {
      return; // Podemos mostrar un mensaje de error aquí
    }
    
    setSaving(true);
    try {
      await updateDoc(doc(db, 'surveys', surveyId), {
        título: survey.título,
        descripción: survey.descripción,
        active: survey.active,
        updatedAt: serverTimestamp(),
        preguntas: survey.preguntas
      });
      
      // Navegar de vuelta a la lista de encuestas
      navigate('/admin/surveys');
    } catch (error) {
      console.error("Error al guardar encuesta:", error);
      // Podemos mostrar un mensaje de error aquí
    } finally {
      setSaving(false);
    }
  };

  // Manejar cambios en los campos de la encuesta
  const handleSurveyChange = (field, value) => {
    setSurvey(prev => ({ ...prev, [field]: value }));
  };

  // Abrir diálogo para añadir/editar pregunta
  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setQuestionText('');
    setQuestionType('multiple');
    setQuestionOptions(['']);
    setQuestionRequired(true);
    setQuestionDialogVisible(true);
  };

  // Abrir diálogo para editar una pregunta existente
  const handleEditQuestion = (question, index) => {
    setEditingQuestion(index);
    setQuestionText(question.texto);
    setQuestionType(question.tipo);
    setQuestionOptions(question.opciones || ['']);
    setQuestionRequired(question.obligatorio);
    setQuestionDialogVisible(true);
  };

  // Eliminar una pregunta
  const handleDeleteQuestion = (index) => {
    // Podemos usar un Dialog de MUI para confirmación si lo deseamos
    const updatedQuestions = [...survey.preguntas];
    updatedQuestions.splice(index, 1);
    setSurvey(prev => ({ ...prev, preguntas: updatedQuestions }));
  };

  // Añadir o actualizar una pregunta
  const handleSaveQuestion = () => {
    if (!questionText.trim()) {
      return; // Podemos mostrar un mensaje de error aquí
    }
    
    if ((questionType === 'multiple' || questionType === 'single') && 
        (!questionOptions.length || !questionOptions.some(opt => opt.trim()))) {
      return; // Podemos mostrar un mensaje de error aquí
    }
    
    const question = {
      id: editingQuestion !== null ? survey.preguntas[editingQuestion].id : generateUniqueId(),
      texto: questionText.trim(),
      tipo: questionType,
      obligatorio: questionRequired
    };
    
    if (questionType === 'multiple' || questionType === 'single') {
      question.opciones = questionOptions.filter(opt => opt.trim());
    }
    
    const updatedQuestions = [...survey.preguntas];
    
    if (editingQuestion !== null) {
      updatedQuestions[editingQuestion] = question;
    } else {
      updatedQuestions.push(question);
    }
    
    setSurvey(prev => ({ ...prev, preguntas: updatedQuestions }));
    setQuestionDialogVisible(false);
  };

  // Añadir opción al listado de opciones
  const handleAddOption = () => {
    setQuestionOptions([...questionOptions, '']);
  };

  // Actualizar texto de una opción
  const handleUpdateOption = (text, index) => {
    const updatedOptions = [...questionOptions];
    updatedOptions[index] = text;
    setQuestionOptions(updatedOptions);
  };

  // Eliminar opción
  const handleRemoveOption = (index) => {
    if (questionOptions.length <= 1) {
      return;
    }
    const updatedOptions = [...questionOptions];
    updatedOptions.splice(index, 1);
    setQuestionOptions(updatedOptions);
  };

  // Cambiar orden de preguntas (mover arriba)
  const handleMoveQuestionUp = (index) => {
    if (index === 0) return;
    
    const updatedQuestions = [...survey.preguntas];
    const temp = updatedQuestions[index];
    updatedQuestions[index] = updatedQuestions[index - 1];
    updatedQuestions[index - 1] = temp;
    
    setSurvey(prev => ({ ...prev, preguntas: updatedQuestions }));
  };

  // Cambiar orden de preguntas (mover abajo)
  const handleMoveQuestionDown = (index) => {
    if (index === survey.preguntas.length - 1) return;
    
    const updatedQuestions = [...survey.preguntas];
    const temp = updatedQuestions[index];
    updatedQuestions[index] = updatedQuestions[index + 1];
    updatedQuestions[index + 1] = temp;
    
    setSurvey(prev => ({ ...prev, preguntas: updatedQuestions }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {survey.id ? 'Editar Encuesta' : 'Nueva Encuesta'}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Información de la Encuesta
            </Typography>
            <TextField
              label="Título"
              fullWidth
              variant="outlined"
              value={survey.título}
              onChange={(e) => handleSurveyChange('título', e.target.value)}
              margin="normal"
              required
            />
            <TextField
              label="Descripción"
              fullWidth
              variant="outlined"
              value={survey.descripción}
              onChange={(e) => handleSurveyChange('descripción', e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={survey.active}
                  onChange={(e) => handleSurveyChange('active', e.target.checked)}
                  color="primary"
                />
              }
              label="Encuesta activa"
              sx={{ mt: 2 }}
            />
          </Paper>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSaveSurvey}
            disabled={saving || !survey.título.trim()}
            sx={{ mb: 2 }}
          >
            {saving ? <CircularProgress size={24} /> : 'Guardar Encuesta'}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => navigate('/admin/surveys')}
          >
            Cancelar
          </Button>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Preguntas
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddQuestion}
              >
                Añadir Pregunta
              </Button>
            </Box>

            {survey.preguntas.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <HelpOutlineIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No hay preguntas en esta encuesta
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                  sx={{ mt: 2 }}
                >
                  Añadir Primera Pregunta
                </Button>
              </Box>
            ) : (
              <List>
                {survey.preguntas.map((question, index) => (
                  <Paper key={question.id} sx={{ mb: 2, overflow: 'hidden' }}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                          <Box 
                            sx={{ 
                              bgcolor: 'primary.main', 
                              color: 'white', 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%', 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              mr: 2, 
                              fontSize: 14,
                              fontWeight: 'bold'
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {question.texto}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                              <Chip
                                label={QUESTION_TYPES.find(t => t.value === question.tipo)?.label || question.tipo}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              {question.obligatorio && (
                                <Chip
                                  label="Obligatoria"
                                  size="small"
                                  color="warning"
                                />
                              )}
                            </Box>

                            {(question.tipo === 'multiple' || question.tipo === 'single') && question.opciones && (
                              <List dense sx={{ pl: 2 }}>
                                {question.opciones.map((option, optIndex) => (
                                  <ListItem key={optIndex} sx={{ py: 0 }}>
                                    {question.tipo === 'multiple' ? (
                                      <Checkbox disabled sx={{ p: 0.5 }} />
                                    ) : (
                                      <Radio disabled sx={{ p: 0.5 }} />
                                    )}
                                    <ListItemText primary={option} />
                                  </ListItem>
                                ))}
                              </List>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                      <Divider />
                      <CardActions>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveQuestionUp(index)}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveQuestionDown(index)}
                          disabled={index === survey.preguntas.length - 1}
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                        <Box sx={{ flex: 1 }} />
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => handleEditQuestion(question, index)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteQuestion(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Paper>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Diálogo para añadir/editar pregunta */}
      <Dialog 
        open={questionDialogVisible} 
        onClose={() => setQuestionDialogVisible(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingQuestion !== null ? 'Editar Pregunta' : 'Añadir Pregunta'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="normal"
            label="Texto de la pregunta"
            fullWidth
            variant="outlined"
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
          />
          
          <FormControl component="fieldset" sx={{ mt: 3, mb: 1 }}>
            <FormLabel component="legend">Tipo de pregunta</FormLabel>
            <RadioGroup
              row
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
            >
              {QUESTION_TYPES.map(type => (
                <FormControlLabel 
                  key={type.value} 
                  value={type.value} 
                  control={<Radio />} 
                  label={type.label} 
                />
              ))}
            </RadioGroup>
          </FormControl>
          
          {(questionType === 'multiple' || questionType === 'single') && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Opciones
              </Typography>
              {questionOptions.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', mb: 2 }}>
                  <TextField
                    label={`Opción ${index + 1}`}
                    fullWidth
                    variant="outlined"
                    value={option}
                    onChange={(e) => handleUpdateOption(e.target.value, index)}
                    size="small"
                  />
                  <IconButton 
                    color="error" 
                    onClick={() => handleRemoveOption(index)}
                    disabled={questionOptions.length <= 1}
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddOption}
                sx={{ mt: 1 }}
              >
                Añadir Opción
              </Button>
            </Box>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={questionRequired}
                onChange={(e) => setQuestionRequired(e.target.checked)}
                color="primary"
              />
            }
            label="Pregunta obligatoria"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuestionDialogVisible(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSaveQuestion} 
            variant="contained"
            disabled={!questionText.trim()}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditSurveyScreen;